import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { hostname, isSafeUrl } from "../../../utils";

interface HubSpotIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    composioConnectedAccountId?: string;
    connectedAt?: string;
    portalId?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    composioConnectedAccountId?: string;
    composioToolkit?: string;
    connected?: boolean;
    stateToken?: string;
  }) => void;
  getToken: () => Promise<string>;
  companyId: string;
}

/**
 * HubSpot Integration Form using Composio OAuth
 *
 * This provides a simple one-click connection flow for HubSpot
 * using Composio's managed OAuth - no need for users to create
 * their own Private App in HubSpot.
 */
export default function HubSpotIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
  getToken,
  companyId,
}: HubSpotIntegrationFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [isConnected, setIsConnected] = useState(false);
  const [stateToken, setStateToken] = useState<string | null>(null);
  const [composioData, setComposioData] = useState<{
    composioConnectedAccountId?: string;
    composioToolkit?: string;
  }>({});

  // Check if already connected (edit mode)
  useEffect(() => {
    if (editMode && initialData?.composioConnectedAccountId) {
      setIsConnected(true);
      setComposioData({
        composioConnectedAccountId: initialData.composioConnectedAccountId,
      });
    }
  }, [editMode, initialData?.composioConnectedAccountId]);

  // Update parent when state changes
  useEffect(() => {
    onValidationChange(isConnected);
    onDataChange({
      connected: isConnected,
      stateToken: stateToken || undefined,
      ...composioData,
    });
  }, [isConnected, stateToken, composioData, onValidationChange, onDataChange]);

  // Handle OAuth flow using Composio
  const handleConnect = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/composio/connect`,
        {
          companyId,
          integrationType: "hubspot",
          instanceName: "HubSpot Account",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.redirectUrl) {
        // Store state token for completion
        setStateToken(response.data.stateToken);

        // Open OAuth in a popup window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (!safeUrlPattern.test(response.data.redirectUrl)) return;
        const popup = window.open(
          response.data.redirectUrl,
          "hubspotOAuth",
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Poll for popup close and check connection status
        const pollInterval = setInterval(async () => {
          if (popup?.closed) {
            clearInterval(pollInterval);

            // Check if connection was successful
            try {
              const currentToken = await getToken();
              const statusResponse = await axios.get(
                `${hostname}/integrations/composio/status?companyId=${companyId}&integrationType=hubspot`,
                {
                  headers: { Authorization: `Bearer ${currentToken}` },
                }
              );

              if (statusResponse.data.connected) {
                // Connection successful
                const connections = statusResponse.data.connections || [];
                const connectedAccount = connections.find(
                  (c: any) => c.status === "ACTIVE"
                );

                if (connectedAccount) {
                  setComposioData({
                    composioConnectedAccountId: connectedAccount.id,
                    composioToolkit: connectedAccount.toolkit,
                  });
                  setIsConnected(true);
                } else {
                  setError("No active connection found. Please try again.");
                }
              } else {
                setError("Connection not completed. Please try again.");
              }
            } catch (err: any) {
              console.error("Error checking connection status:", err);
              setError(
                err.response?.data?.error ||
                  "Failed to verify connection. Please try again."
              );
            }

            setLoading(false);
          }
        }, 1000);

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          clearInterval(pollInterval);
          setError(
            "Popup was blocked. Please allow popups for this site and try again."
          );
          setLoading(false);
        }
      }
    } catch (err: any) {
      console.error("Error starting HubSpot OAuth:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to start connection. Please try again."
      );
      setLoading(false);
    }
  }, [companyId, getToken]);

  // In edit mode with existing connection, show connected status
  if (editMode && isConnected) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="body2">
              HubSpot account connected
            </Typography>
          </Box>
          {initialData?.connectedAt && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Connected on:{" "}
              {new Date(initialData.connectedAt).toLocaleDateString()}
            </Typography>
          )}
          {initialData?.portalId && (
            <Typography variant="caption" display="block">
              Portal ID: {initialData.portalId}
            </Typography>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          To reconnect with a different account, remove this integration and
          create a new one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Connect your HubSpot account to sync contacts and log activities
        when leads reply to your sequences.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Connect your HubSpot account with one click. No Private App setup
          or developer account required.
        </Typography>
      </Alert>

      {!isConnected ? (
        <>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={loading}
            startIcon={
              loading && <CircularProgress size={16} color="inherit" />
            }
            fullWidth
            size="large"
            sx={{ mb: 2 }}
          >
            {loading ? "Connecting..." : "Connect HubSpot Account"}
          </Button>

          <Alert severity="warning">
            <Typography variant="caption">
              <strong>Note:</strong> A popup will open for you to authorize
              access to your HubSpot account. Make sure pop-ups are not blocked.
            </Typography>
          </Alert>
        </>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="body2">
              HubSpot connected successfully! Click "Install" to save.
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>
  );
}
